import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { EMPTY, of } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';

import { CandidateAnalytics } from '@core/candidate-util';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type {
  CandidateProfileMoreHotspot,
  IntegrationKinds,
  Partner,
} from '@core/integrations-types';
import type { DialogComponentI, NotificationData } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  NotificationsController,
  RtStatefulComponent,
  WindowRef,
} from '@core/user-interface-legacy';

import {
  Account,
  ContextualIntegrationsApiService,
  CurrentHotspotToken,
  IntegrationKindToken,
  PartnerToken,
} from '../../client/integrations-client.module';
import {
  CoreApiService,
  Destroyable,
  HttpErrorHandlerService,
  takeUntilDestroyed,
} from '../../common';
import { IntegrationEventActions } from '../../common/events/events';
import { ErrorMessagesWrapperComponent } from '../../misc/error-messages-wrapper/error-messages-wrapper.component';
import type { CandidateEmailInput } from '../inputs/candidate-email-input/candidate-email-input.component';
import { DynamicForm } from '../inputs/dynamic-form/dynamic-form';
import { DynamicFormComponent } from '../inputs/dynamic-form/dynamic-form.component';
import type { Candidate, DynamicFormConfig } from '../types';
import { DynamicInputConfigType, findInputConfigIndex } from '../types';

type State = {
  title: string;
  subtitle: string | null;
  form: DynamicForm;
  candidate: Candidate;
  formLoading: boolean;
  saving: boolean;
  errors: string[];
};

type DialogData = {
  title: string;
  subtitle: string | null;
  formConfig: DynamicFormConfig;
  candidate: Candidate;
};

@Component({
  selector: 'rti-send-candidate-dialog',
  templateUrl: './send-candidate-dialog.component.html',
  styleUrls: ['./send-candidate-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DynamicFormComponent,
    ErrorMessagesWrapperComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class SendCandidateDialogComponent
  extends RtStatefulComponent<State>
  implements DialogComponentI<DialogData, boolean>
{
  private readonly dialog = inject(DialogRef);
  private readonly coreApi = inject(CoreApiService);
  private readonly api = inject(ContextualIntegrationsApiService);
  private readonly notificationsCtrl = inject(NotificationsController);
  private readonly i18n = inject(I18nService);
  private readonly httpErrorHandlerService = inject(HttpErrorHandlerService);
  private readonly store = inject(Store);
  private readonly window = inject(WindowRef);
  private readonly account = inject(Account);
  private readonly currentHotspot = inject<CandidateProfileMoreHotspot>(CurrentHotspotToken);
  private readonly integrationKind = inject<IntegrationKinds>(IntegrationKindToken);
  private readonly partner = inject<Partner>(PartnerToken);
  private readonly candidateAnalytics = inject(CandidateAnalytics);

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);

    this.createState({
      title: null!,
      subtitle: null!,
      form: null!,
      candidate: null!,
      formLoading: false,
      saving: false,
      errors: [],
    });
  }

  public setDialogData(data: DialogData): void {
    this.validateFormConfigCohesion(data.formConfig);

    this.setState(
      {
        ...this.state,
        title: data.title,
        subtitle: data.subtitle,
        candidate: data.candidate,
      },
      false,
    );

    this.initializeForm(data.formConfig);
  }

  public close(): void {
    this.dialog.close();
  }

  public send(): void {
    this.candidateAnalytics.trackSendToKiwiIntegrationDialog();

    this.maybeSetCandidateEmail()
      .pipe(switchMap(() => this.sendCandidate(this.formValue)))
      .subscribe();
  }

  private initializeForm(config: DynamicFormConfig): void {
    const form = this.buildDynamicForm(config);

    form.valueChanges.pipe(takeUntilDestroyed(this)).subscribe(() => this.cdRef.detectChanges());

    form.loading$.pipe(takeUntilDestroyed(this)).subscribe(loading => {
      this.setState({ ...this.state, formLoading: loading });
    });
    this.setState({ ...this.state, form });
  }

  private buildDynamicForm(config: DynamicFormConfig): DynamicForm {
    let result = config;

    const placementsInputConfigIndex = findInputConfigIndex(
      DynamicInputConfigType.CANDIDATE_PLACEMENT,
      config,
    );
    if (placementsInputConfigIndex >= 0) {
      result = Object.assign([], result, {
        [placementsInputConfigIndex]: {
          ...result[placementsInputConfigIndex],
          placements: this.state.candidate.placements,
        },
      });
    }

    const adminsInputConfigIndex = findInputConfigIndex(DynamicInputConfigType.ADMINS, config);
    if (adminsInputConfigIndex >= 0) {
      result = Object.assign([], result, {
        [adminsInputConfigIndex]: {
          ...result[adminsInputConfigIndex],
          value: [this.account.admin],
        },
      });
    }

    return new DynamicForm(result);
  }

  private validateFormConfigCohesion(formConfig: DynamicFormConfig): void {
    if (formConfig.filter(i => i.type === DynamicInputConfigType.CANDIDATE_EMAIL).length > 1)
      throw new Error(`Form has more than 1 ${DynamicInputConfigType.CANDIDATE_EMAIL} inputs`);
  }

  private maybeSetCandidateEmail(): Observable<any> {
    const input = this.candidateEmailInput;

    if (input && !input.config.emails.length) {
      this.setState({ ...this.state, saving: true, errors: [] });

      return this.setCandidateEmail(input.control.value).pipe(
        tap(() => {
          this.setState({ ...this.state, saving: false });
        }),
        catchError(error => {
          this.setState({
            ...this.state,
            saving: false,
            errors: error.error ? error.error.error || [] : [],
          });
          return EMPTY;
        }),
      );
    } else {
      return of(null);
    }
  }

  private get candidateEmailInput(): CandidateEmailInput | null {
    return <CandidateEmailInput>(
      this.state.form.inputs.find(i => i.config.type === DynamicInputConfigType.CANDIDATE_EMAIL)
    );
  }

  private setCandidateEmail(email: string): Observable<any> {
    return this.coreApi
      .patch(`candidates/${this.state.candidate.id}`, {
        candidate: { emails: [email] },
      })
      .pipe(takeUntilDestroyed(this));
  }

  private get formValue(): {} {
    return this.state.form.value;
  }

  private get partnerName(): string {
    if (this.partner) {
      return this.partner.name;
    } else {
      return this.i18n.translate(`rti.${this.integrationKind}.name`);
    }
  }

  private sendCandidate(sendCandidateForm: any): Observable<any> {
    const formParams = Object.keys(sendCandidateForm).reduce(
      (acc, key) => {
        const value = sendCandidateForm[key];
        if (value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      <any>{},
    );

    this.setState({ ...this.state, saving: true });

    const jsonParams =
      this.partner && this.partner.kombo ? { _formJson: JSON.stringify(formParams) } : {};
    const params = { ...formParams, ...jsonParams };

    return this.api.post(`candidates/${this.state.candidate.id}/send-candidate`, params).pipe(
      tap(
        (result: { url: string | null; form: DynamicFormConfig | undefined }) => {
          this.store.dispatch(
            IntegrationEventActions.CandidateSentToPartnerEvent({
              payload: {
                context: {
                  hotspot: this.currentHotspot,
                  integrationKind: this.integrationKind,
                  partner: this.partner,
                },
              },
            }),
          );
          const notificationData: NotificationData = {
            title: this.i18n.translate('rti.dynamic.send_candidate.toast.sent', {
              partnerName: this.partnerName,
            }),
          };

          if (result.url)
            notificationData['topButtonLabel'] = this.i18n.translate('rti.common.view');

          const sendingSuccessNotification = this.notificationsCtrl.success(notificationData);

          sendingSuccessNotification
            .topButtonClick()
            .pipe(takeUntil(sendingSuccessNotification.beforeClose))
            .subscribe(() => {
              if (result.url) this.window.open(result.url);
            });

          this.close();
        },
        errorResponse => {
          this.setState({ ...this.state, saving: false });

          if (errorResponse.status === 409) {
            const msg = this.i18n.translate(
              `rti.${this.integrationKind}.send_candidate.already_sent`,
            );

            if (msg.startsWith('missing translation')) {
              this.httpErrorHandlerService.default(errorResponse);
            } else {
              this.notificationsCtrl.success(msg);
            }
          } else if (errorResponse.status === 422 && errorResponse.error?.error?.length) {
            this.setState({ ...this.state, errors: errorResponse.error.error });
          } else {
            this.httpErrorHandlerService.default(errorResponse);
          }
        },
      ),
    );
  }
}
